import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { jobsService, staffService, imagesService, quotesService, appointmentsService, servicesService, reviewsService, quoteRequestsService, worksheetService, contentService } from '../services/api';
import { todayBrisbane } from '../utils/costs';
import { JobModal } from '../components/JobModal';
import { QuoteModal } from '../components/QuoteModal';
import { AppointmentModal } from '../components/AppointmentModal';
import { JobCosts } from '../components/JobCosts';
import { MaterialsList } from '../components/MaterialsList';
import Lightbox from '../components/Lightbox/Lightbox';
import { formatApptDateLong, formatApptTime, formatApptRange } from '../utils/dateFormatters';
import { formatMoney } from '../utils/pricing';
import { JOB_STATUSES, jobStatusLabel, jobStatusClass } from '../utils/jobs';
import { endTimeFor, addMinutesToTime, minutesBetween, spanDates, describeSpan, spanResultLabel, fmtDayYmd, fmtHours, addDaysYmd, nextWorkingDay, DEFAULT_DAY_MINUTES } from '../utils/schedule';
import { monthLabel } from '../utils/businessCosts';
import './JobDetail.css';

const QUOTE_STATUS_LABEL = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' };
const PHOTO_KIND_LABEL = { customer: 'From the client', before: 'Before', after: 'After' };
const LIVE = (v) => !['cancelled', 'no_show'].includes(v.status);
const tomorrow = () => addDaysYmd(todayBrisbane(), 1);
const fmtDay = fmtDayYmd;
// "-$227.46" rather than "$-227.46" for a loss
const money = (v) => (Number(v) < 0 ? `-${formatMoney(Math.abs(Number(v)))}` : formatMoney(v));

/**
 * One job in full: the client, its quotes, its visits (a job can run over
 * several days), its costs and P&L, and the photos across every visit.
 */
export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [plan, setPlan] = useState(null); // the span scheduler: { service_id, start_date, start_time, end_date, end_time, skip_weekends, ignore_hours, staff_ids, results, busy }
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [quoteModal, setQuoteModal] = useState(null); // { quote } | { prefill }
  const [visitModal, setVisitModal] = useState(null); // { appointment } | { prefill }
  const [lightbox, setLightbox] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attach, setAttach] = useState(null); // null | { candidates, pick, all }  — quotes
  const [attachV, setAttachV] = useState(null); // same shape — visits
  const [ovr, setOvr] = useState(null); // overheads override editor: { pct } (GID-Session-015)
  const [worksheet, setWorksheet] = useState(null); // the job's quote with its costings estimate + materials count (GID-Session-024)
  const [wsTick, setWsTick] = useState(0); // remounts the materials list after the quote was edited

  const load = useCallback(async () => {
    const [r, s, sv, ws] = await Promise.all([jobsService.getById(id), staffService.getAll(), servicesService.getAll(), worksheetService.forJob(id)]);
    setJob(r.success ? r.job : null);
    setWorksheet(ws.success ? ws.worksheet : null);
    setWsTick((t) => t + 1);
    if (s.success) setStaff((s.staff || []).filter((p) => p.is_active));
    if (sv.success) setServices((sv.services || []).filter((x) => x.is_active));
    setLoading(false);
  }, [id]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  // Content Studio (GID-Session-025): the customer's OK for socials, and a post from this job.
  async function toggleSocials() {
    const r = await contentService.setSocials(job.id, !job.socials_ok);
    if (r.success) setJob((j) => ({ ...j, socials_ok: r.job.socials_ok }));
    else setError(r.error);
  }
  async function makePost() {
    const shots = job.photos.filter((p) => p.kind === 'before' || p.kind === 'after');
    const r = await contentService.create({ status: 'draft', title: job.title || job.job_number, job_id: job.id, pillar: shots.length ? 'glow_up' : null, image_ids: shots.slice(0, 4).map((p) => p.id) });
    if (r.success) navigate(`/content?post=${r.post.id}`);
    else setError(r.error);
  }

  async function changeStatus(status) {
    setBusy(true);
    setError('');
    const r = await jobsService.update(id, { status });
    setBusy(false);
    if (r.success) setJob(r.job);
    else setError(r.error || 'Could not change the status');
  }
  // Overheads: this job carries its own % of revenue instead of the standard share (null = standard).
  async function saveOverride(pct) {
    setBusy(true);
    setError('');
    const r = await jobsService.update(id, { overhead_pct_override: pct === null || pct === '' ? null : Number(pct) });
    setBusy(false);
    if (r.success) { setJob(r.job); setOvr(null); } else setError(r.error || 'Could not change the overheads');
  }
  async function remove() {
    if (!window.confirm(`Delete ${job.job_number}? Its cost lines and receipts go with it. This cannot be undone.`)) return;
    setBusy(true);
    const r = await jobsService.delete(id);
    setBusy(false);
    if (r.success) navigate('/jobs');
    else setError(r.error || 'Could not delete the job');
  }

  if (loading) return <div className="empty-state"><div className="loading-spinner"></div></div>;
  if (!job) return <div className="empty-state"><p>Job not found</p><Link to="/jobs" className="btn btn-outline">Back to jobs</Link></div>;

  const c = job.customer;
  const addr = c ? [c.address_street, c.address_suburb, c.address_state, c.address_postcode].filter(Boolean).join(', ') : '';
  const canDelete = job.visits.length === 0 && job.quotes.length === 0;
  const lastVisit = job.visits[job.visits.length - 1];
  // The schedule = the live visits, first to last.
  const live = job.visits.filter(LIVE);
  const firstLive = live[0];
  const lastLive = live[live.length - 1];
  const liveMinutes = live.reduce((sum, v) => sum + (Number(v.duration_minutes) || 0), 0);
  // "Fri, 11 Sept 8:00 am → Tue, 15 Sept 4:00 pm · 3 visits · 24 h" (first start → last end).
  const scheduleText = live.length
    ? `${formatApptDateLong(firstLive.starts_at)} ${formatApptTime(firstLive.starts_at)} → ${live.length > 1 ? `${formatApptDateLong(lastLive.starts_at)} ` : ''}${formatApptTime(lastLive.ends_at)} · ${live.length} visit${live.length === 1 ? '' : 's'} · ${fmtHours(liveMinutes)}`
    : 'Not scheduled yet';

  // Span scheduler: a start date and time to an end date and time — one visit
  // per working day, each day blocked start → end, so the calendar holds the job.
  function openPlan() {
    const svcId = lastVisit?.service_id ? String(lastVisit.service_id) : (services[0] ? String(services[0].id) : '');
    const svc = services.find((x) => String(x.id) === svcId);
    const day = nextWorkingDay(tomorrow());
    setPlan({ service_id: svcId, start_date: day, start_time: '08:00', end_date: day, end_time: endTimeFor(svc, '08:00'), skip_weekends: true, ignore_hours: false, staff_ids: svc?.staff_ids || [], results: null, busy: false });
  }
  const setPlanField = (k, v) => setPlan((p) => {
    const next = { ...p, [k]: v };
    if (k === 'start_date' && (!p.end_date || p.end_date < v)) next.end_date = v; // the end follows the start until it is set
    if (k === 'start_time') next.end_time = addMinutesToTime(v, minutesBetween(p.start_time, p.end_time) || DEFAULT_DAY_MINUTES); // keep the day's length
    return next;
  });
  function planService(svcId) {
    const svc = services.find((x) => String(x.id) === svcId);
    setPlan((p) => ({ ...p, service_id: svcId, staff_ids: svc?.staff_ids || [], end_time: endTimeFor(svc, p.start_time) }));
  }
  const planDays = plan ? spanDates(plan.start_date, plan.end_date || plan.start_date, plan.skip_weekends).length : 0;
  async function submitPlan() {
    if (!plan || plan.busy) return;
    if (!plan.service_id || !plan.start_date || !plan.start_time || !plan.end_time) { setError('Pick a job type, a start date and time, and an end time'); return; }
    if ((minutesBetween(plan.start_time, plan.end_time) || 0) <= 0) { setError('The end time has to be after the start time'); return; }
    if (plan.end_date && plan.end_date < plan.start_date) { setError('The end date has to be on or after the start date'); return; }
    if (!planDays) { setError('Every day in that span is a weekend — untick "Skip weekends" or pick other days'); return; }
    if (!plan.staff_ids.length) { setError('Pick who is on it'); return; }
    setError('');
    setPlan((p) => ({ ...p, busy: true }));
    const r = await jobsService.schedule(job.id, { service_id: Number(plan.service_id), start_date: plan.start_date, start_time: plan.start_time, end_date: plan.end_date || plan.start_date, end_time: plan.end_time, skip_weekends: plan.skip_weekends, ignore_hours: plan.ignore_hours, staff_ids: plan.staff_ids });
    if (!r.success) { setPlan((p) => ({ ...p, busy: false })); setError(r.error || 'Could not schedule the visits'); return; }
    setJob(r.job);
    if (r.results.every((x) => x.ok)) setPlan(null);
    else setPlan((p) => ({ ...p, busy: false, results: r.results })); // show which days did not book
  }
  const visitById = Object.fromEntries(job.visits.map((v) => [v.id, v]));
  const photoGroups = [...new Set(job.photos.map((p) => p.appointment_id))].map((aid) => ({ visit: visitById[aid], photos: job.photos.filter((p) => p.appointment_id === aid) }));
  const s = job.costs?.summary;
  const est = worksheet && worksheet.line_count > 0 ? worksheet.summary : null; // the quote's costings, shown beside the actual P&L

  function addVisit() {
    setVisitModal({ prefill: { customer_id: job.customer_id, job_id: job.id, service_id: lastVisit?.service_id ? String(lastVisit.service_id) : '', admin_notes: `Job ${job.job_number}${job.title ? ` — ${job.title}` : ''}` } });
  }
  function newQuote() {
    setQuoteModal({ prefill: { customer_id: job.customer_id, job_id: job.id, title: job.title || '' } });
  }
  // Attach a quote that already exists (it moves here with its visits; an emptied job is removed).
  async function openAttach(all = false) {
    const r = await quotesService.getAll(all || !job.customer_id ? {} : { customerId: job.customer_id });
    const candidates = (r.quotes || []).filter((q) => q.job_id !== job.id);
    setAttach({ candidates, pick: candidates[0] ? String(candidates[0].id) : '', all: all || !job.customer_id });
  }
  async function attachQuote() {
    if (!attach?.pick) return;
    setBusy(true);
    setError('');
    const r = await jobsService.attachQuote(job.id, Number(attach.pick));
    setBusy(false);
    if (r.success) { setAttach(null); setJob(r.job); } else setError(r.error || 'Could not attach the quote');
  }
  // Attach a visit that already exists (it moves here with its cost lines; an emptied job is removed).
  async function openAttachVisit(all = false) {
    const r = await appointmentsService.getAll(all || !job.customer_id ? {} : { customerId: job.customer_id });
    const candidates = (r.appointments || []).filter((a) => a.job_id !== job.id);
    setAttachV({ candidates, pick: candidates[0] ? String(candidates[0].id) : '', all: all || !job.customer_id });
  }
  async function attachVisit() {
    if (!attachV?.pick) return;
    setBusy(true);
    setError('');
    const r = await jobsService.attachVisit(job.id, Number(attachV.pick));
    setBusy(false);
    if (r.success) { setAttachV(null); setJob(r.job); } else setError(r.error || 'Could not attach the visit');
  }
  // Review request ask (backlog #612) — the display of reviews on the website is separate.
  async function sendReview() {
    setBusy(true);
    setError('');
    const r = await reviewsService.send(job.id);
    setBusy(false);
    if (r.success) load();
    else setError(r.error || 'Could not send the review request');
  }
  async function cancelReview() {
    setBusy(true);
    setError('');
    const r = await reviewsService.cancel(job.id);
    setBusy(false);
    if (r.success) load();
    else setError(r.error || 'Could not cancel the review request');
  }
  function reviewBlock() {
    if (!job.review) return null;
    const r = job.review.request;
    if (!(job.status === 'complete' || job.status === 'invoiced' || r)) return null;
    if (!r) {
      const noEmail = !c?.email;
      const notConfigured = !job.review.configured;
      const hint = notConfigured ? 'Add your Google review link under Settings' : noEmail ? 'This client has no email address' : '';
      return (
        <p className="job-review">
          <span className="text-muted">Review request: not sent</span>
          <button type="button" className="btn btn-sm btn-outline" disabled={busy || notConfigured || noEmail} title={hint || undefined} onClick={sendReview}>Send review request</button>
          {hint && <span className="text-muted job-review-hint">{hint}</span>}
        </p>
      );
    }
    const when = `${formatApptDateLong(r.send_at)} ${formatApptTime(r.send_at)}`;
    if (r.status === 'queued') {
      return (
        <p className="job-review">
          <span className="text-muted">Review request queued for {when} to {r.recipient_email}</span>
          <button type="button" className="btn btn-sm btn-outline" disabled={busy} onClick={cancelReview}>Don't send</button>
        </p>
      );
    }
    if (r.status === 'sent') {
      return (
        <p className="job-review">
          <span className="text-muted">Review request sent {formatApptDateLong(r.sent_at)} to {r.recipient_email}</span>
          <button type="button" className="btn btn-sm btn-outline" disabled={busy} onClick={sendReview}>Send again</button>
        </p>
      );
    }
    if (r.status === 'cancelled') {
      return (
        <p className="job-review">
          <span className="text-muted">Review request cancelled</span>
          <button type="button" className="btn btn-sm btn-outline" disabled={busy} onClick={sendReview}>Send now</button>
        </p>
      );
    }
    return (
      <p className="job-review">
        <span className="text-muted">Review request failed: {r.error}</span>
        <button type="button" className="btn btn-sm btn-outline" disabled={busy} onClick={sendReview}>Try again</button>
      </p>
    );
  }
  function openPhotos(group, index) {
    setLightbox({
      images: group.photos.map((p) => ({ src: imagesService.fileUrl(p.id), label: `${PHOTO_KIND_LABEL[p.kind] || p.kind}${group.visit ? ` — ${formatApptDateLong(group.visit.starts_at)}` : ''}${p.original_name ? ` — ${p.original_name}` : ''}` })),
      index
    });
  }

  return (
    <div className="job-detail">
      <div className="page-header">
        <div>
          <Link to="/jobs" className="back-link">← Jobs</Link>
          <p className="job-kicker">{job.job_number}</p>
          <h1 className="page-title">{job.title || 'Untitled job'}</h1>
          <p className={`job-schedule-line ${live.length ? '' : 'text-muted'}`}>
            {live.length ? <><span className="job-schedule-label">Scheduled</span> {scheduleText}</> : <>Not scheduled yet — <button type="button" className="btn-link job-inline-link" onClick={openPlan}>set a date and time</button></>}
          </p>
        </div>
        <div className="page-actions">
          <select className="job-status-select" aria-label="Job status" value={job.status} onChange={(e) => changeStatus(e.target.value)} disabled={busy}>
            {JOB_STATUSES.map((st) => <option key={st.value} value={st.value}>{st.label}</option>)}
          </select>
          <button className="btn btn-outline" onClick={newQuote}>+ Quote</button>
          <button className="btn btn-outline" onClick={addVisit}>+ Visit</button>
          <button className="btn btn-outline" onClick={() => setEditing(true)}>Edit</button>
          <button className="btn btn-danger" onClick={remove} disabled={!canDelete || busy} title={canDelete ? 'Delete this job' : 'Remove its visits and quotes first'}>Delete</button>
        </div>
      </div>
      {error && <p className="form-error job-hint">{error}</p>}

      {s && (
        <>
          <div className="job-pl">
            <div className="job-pl-tile"><span>{s.revenue_source === 'quote' ? `Quoted · ${s.quote_number}` : s.revenue_source ? 'Job type price' : 'Quoted'}</span><strong>{s.revenue === null ? '—' : formatMoney(s.revenue)}</strong></div>
            <div className="job-pl-tile"><span>Costs</span><strong>{formatMoney(s.total)}</strong>{s.cost_per_m2 !== null && <em>{formatMoney(s.cost_per_m2)}/m² of {s.m2} m²</em>}{est && <em className="job-pl-est" title="From the costings on the quote">est. {formatMoney(est.costs)} on the quote</em>}</div>
            <button type="button" className={`job-pl-tile job-pl-tile-btn ${ovr ? 'active' : ''}`} onClick={() => setOvr(ovr ? null : { pct: s.overhead?.override_pct ?? '' })} title="The job's share of the business's general costs — click to give this job its own %">
              <span>Overheads{s.overhead?.override_pct !== null && s.overhead?.override_pct !== undefined ? ' · own %' : ''}</span>
              <strong>{s.overhead_share === null || s.overhead_share === undefined ? '—' : formatMoney(s.overhead_share)}</strong>
              {s.overhead?.working && <em>{s.overhead.working}</em>}
            </button>
            <div className={`job-pl-tile ${s.profit === null || s.profit === undefined ? '' : s.profit < 0 ? 'neg' : 'pos'}`}>
              <span>Profit</span>
              <strong>{s.profit === null || s.profit === undefined ? '—' : money(s.profit)}</strong>
              {est && <em className="job-pl-est" title="From the costings on the quote">est. {money(est.profit)}{est.margin_pct !== null ? ` (${est.margin_pct}%)` : ''} on the quote</em>}
              {s.profit_pct !== null && s.profit_pct !== undefined && <em>{s.profit_pct}%{s.overhead_share ? ` · ${money(s.margin)} before overheads` : ''}</em>}
              {job.counts_in_reports && job.done_on && <em><Link to={`/reports?month=${job.done_on.slice(0, 7)}`}>In {monthLabel(job.done_on.slice(0, 7))}'s P&L →</Link></em>}
            </div>
            <div className="job-pl-tile">
              <span>Schedule</span>
              <strong>{firstLive ? formatApptDateLong(firstLive.starts_at) : '—'}</strong>
              {firstLive && <em>{formatApptTime(firstLive.starts_at)} → {live.length > 1 ? `${formatApptDateLong(lastLive.starts_at)} ` : ''}{formatApptTime(lastLive.ends_at)} · {live.length} visit{live.length === 1 ? '' : 's'}</em>}
            </div>
            <div className="job-pl-tile"><span>Status</span><strong className={`status-badge ${jobStatusClass(job.status)}`}>{jobStatusLabel(job.status)}</strong></div>
          </div>
          {ovr && (
            <div className="job-ovr">
              <span className="text-muted">Overheads on this job:</span>
              <label className="job-ovr-pct">its own
                <input type="number" min="0" step="0.5" aria-label="Overheads as a percent of revenue" value={ovr.pct} onChange={(e) => setOvr({ pct: e.target.value })} placeholder={s.overhead?.basis === 'revenue' && s.overhead?.pct_of_revenue !== null ? String(s.overhead.pct_of_revenue) : '10'} />
                % of {s.revenue === null ? 'its revenue' : formatMoney(s.revenue)}{ovr.pct !== '' && s.revenue !== null ? ` = ${formatMoney((s.revenue * (Number(ovr.pct) || 0)) / 100)}` : ''}
              </label>
              <span className="text-muted job-ovr-hint">{s.revenue === null ? 'Nothing quoted or priced yet, so a % has nothing to work from.' : 'The standard share comes from the rate on Business costs.'}</span>
              <span className="spacer" />
              <button type="button" className="btn btn-sm btn-outline" onClick={() => setOvr(null)} disabled={busy}>Cancel</button>
              {s.overhead?.override_pct !== null && s.overhead?.override_pct !== undefined && <button type="button" className="btn btn-sm btn-outline" onClick={() => saveOverride(null)} disabled={busy}>Back to the standard share</button>}
              <button type="button" className="btn btn-sm btn-primary" onClick={() => saveOverride(ovr.pct)} disabled={busy || ovr.pct === ''}>Use this %</button>
            </div>
          )}
        </>
      )}

      <div className="job-grid">
        <section className="card">
          <h2 className="card-title mb-md">Client</h2>
          {c ? (
            <>
              <p><Link to={`/clients/${c.id}`}><strong>{c.name}</strong></Link></p>
              {c.phone && <p><a className="click-to-call" href={`tel:${c.phone}`}>{c.phone}</a></p>}
              {c.email && <p><a className="click-to-call" href={`mailto:${c.email}`}>{c.email}</a></p>}
              {addr && <p className="text-muted"><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`} target="_blank" rel="noopener noreferrer">{addr} ↗</a></p>}
            </>
          ) : <p className="text-muted">No client on this job yet — Edit to pick one.</p>}
          {job.request && (
            <div className="job-request">
              <h2 className="card-title mt-lg mb-md">Quote request</h2>
              <p className="text-muted job-meta">Sent {formatApptDateLong(job.request.created_at.replace(' ', 'T') + 'Z')} from the website · came from {job.request.source_label}</p>
              {job.request.work.length > 0 && <p><strong>{job.request.work.join(' · ')}</strong></p>}
              {job.request.details && <p className="job-notes">“{job.request.details}”</p>}
              {job.request.images.length > 0 && (
                <div className="job-photo-thumbs">
                  {job.request.images.map((img, i) => (
                    <button key={img.id} type="button" className="job-photo-thumb" title={img.original_name || 'Photo from the client'}
                      onClick={() => setLightbox({ images: job.request.images.map((x) => ({ src: quoteRequestsService.imageUrl(x.id), label: `From the client${x.original_name ? ` — ${x.original_name}` : ''}` })), index: i })}>
                      <img src={quoteRequestsService.imageUrl(img.id, 'thumb')} alt={img.original_name || 'Photo from the client'} />
                    </button>
                  ))}
                </div>
              )}
              {job.status === 'enquiry' && job.quotes.length === 0 && (
                <p className="job-request-next">Next: <button type="button" className="link-btn" onClick={newQuote}>measure it and write the quote</button> with Smart Quote, then Send.</p>
              )}
            </div>
          )}
          <h2 className="card-title mt-lg mb-md">Notes</h2>
          {job.notes ? <p className="job-notes">{job.notes}</p> : <p className="text-muted">Nothing noted.</p>}
          <p className="text-muted job-meta">Opened {formatApptDateLong(job.created_at)}{job.created_by_name ? ` by ${job.created_by_name}` : ''}</p>
          {reviewBlock()}
        </section>

        <section className="card">
          <div className="job-card-head">
            <h2 className="card-title">Quotes ({job.quotes.length})</h2>
            <div className="job-card-actions">
              <button type="button" className="btn btn-sm btn-outline" onClick={() => (attach ? setAttach(null) : openAttach())}>{attach ? 'Cancel' : 'Attach existing'}</button>
              <button type="button" className="btn btn-sm btn-outline" onClick={newQuote}>+ New quote</button>
            </div>
          </div>
          {attach && (
            <div className="job-attach">
              {attach.candidates.length === 0 ? (
                <p className="text-muted">{attach.all ? 'No other quotes to attach.' : 'No other quotes for this client.'}</p>
              ) : (
                <div className="job-attach-row">
                  <select aria-label="Quote to attach" value={attach.pick} onChange={(e) => setAttach((a) => ({ ...a, pick: e.target.value }))}>
                    {attach.candidates.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.quote_number} · {q.title || `${q.item_count} line item${Number(q.item_count) === 1 ? '' : 's'}`} · {formatMoney(q.total)} · {QUOTE_STATUS_LABEL[q.effective_status] || q.effective_status}{q.job_number ? ` · on ${q.job_number}` : ''}{attach.all && q.customer_name ? ` · ${q.customer_name}` : ''}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-sm btn-primary" onClick={attachQuote} disabled={busy || !attach.pick}>Attach</button>
                </div>
              )}
              {!attach.all && job.customer_id && (
                <button type="button" className="btn-link job-attach-all" onClick={() => openAttach(true)}>Show every client's quotes</button>
              )}
              <p className="text-muted job-attach-hint">The quote moves here with the visits it came from or booked. A job left with nothing on it is removed.</p>
            </div>
          )}
          {job.quotes.length === 0 ? <p className="text-muted">No quotes on this job yet.</p> : (
            <div className="job-rows">
              {job.quotes.map((q) => (
                <button key={q.id} type="button" className="job-item" onClick={() => setQuoteModal({ quote: q })}>
                  <div className="job-item-when"><strong>{q.quote_number}</strong><span className="text-muted">{formatApptDateLong(q.sent_at || q.created_at)}</span></div>
                  <span className="job-item-main">{q.title || `${q.item_count} line item${Number(q.item_count) === 1 ? '' : 's'}`} · {formatMoney(q.total)}</span>
                  <span className={`status-badge status-${q.effective_status}`}>{QUOTE_STATUS_LABEL[q.effective_status] || q.effective_status}</span>
                </button>
              ))}
            </div>
          )}

          <div className="job-card-head mt-lg">
            <h2 className="card-title">Schedule ({job.visits.length})</h2>
            <div className="job-card-actions">
              <button type="button" className="btn btn-sm btn-outline" onClick={() => (attachV ? setAttachV(null) : openAttachVisit())}>{attachV ? 'Cancel' : 'Attach existing'}</button>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => (plan ? setPlan(null) : openPlan())}>{plan ? 'Cancel' : '+ Schedule'}</button>
            </div>
          </div>
          <p className={`job-schedule-summary ${live.length ? '' : 'text-muted'}`}>{scheduleText}</p>
          {plan && (
            <div className="job-plan">
              <div className="job-plan-row">
                <label>Job type
                  <select value={plan.service_id} onChange={(e) => planService(e.target.value)}>
                    <option value="">Select…</option>
                    {services.map((x) => <option key={x.id} value={x.id}>{x.name}{x.duration_minutes ? ` (${x.duration_minutes} min)` : ''}</option>)}
                  </select>
                </label>
              </div>
              <div className="job-plan-row job-plan-when">
                <label>Start date<input type="date" value={plan.start_date} onChange={(e) => setPlanField('start_date', e.target.value)} /></label>
                <label>Start time<input type="time" value={plan.start_time} onChange={(e) => setPlanField('start_time', e.target.value)} /></label>
                <label>End date<input type="date" min={plan.start_date || undefined} value={plan.end_date} onChange={(e) => setPlanField('end_date', e.target.value)} /></label>
                <label>End time<input type="time" value={plan.end_time} onChange={(e) => setPlanField('end_time', e.target.value)} /></label>
              </div>
              <div className="job-plan-row job-plan-crew">
                <span className="text-muted">Who's on it</span>
                {staff.map((p) => (
                  <label key={p.id} className={`crew-option ${plan.staff_ids.includes(p.id) ? 'on' : ''}`}>
                    <input type="checkbox" checked={plan.staff_ids.includes(p.id)} onChange={() => setPlanField('staff_ids', plan.staff_ids.includes(p.id) ? plan.staff_ids.filter((x) => x !== p.id) : [...plan.staff_ids, p.id])} />
                    <span className="crew-dot" style={{ background: p.color || 'var(--color-teal)' }} />
                    {p.name}
                  </label>
                ))}
                <label className="checkbox-label job-plan-weekends">
                  <input type="checkbox" checked={plan.skip_weekends} onChange={(e) => setPlanField('skip_weekends', e.target.checked)} /> Skip weekends
                </label>
                <label className="checkbox-label job-plan-weekends" title="Book even when the crew's roster (Availability) does not cover the time. Clashes with other visits still count.">
                  <input type="checkbox" checked={plan.ignore_hours} onChange={(e) => setPlanField('ignore_hours', e.target.checked)} /> Outside working hours is OK
                </label>
              </div>
              {plan.results && (
                <ul className="job-plan-results">
                  {plan.results.map((x) => <li key={x.date} className={x.ok ? 'ok' : 'bad'}>{fmtDay(x.date)}: {spanResultLabel(x)}</li>)}
                  {!plan.ignore_hours && plan.results.some((x) => x.code === 'OUTSIDE_HOURS') && (
                    <li className="text-muted">Tick "Outside working hours is OK" to book anyway, or widen the roster under Availability.</li>
                  )}
                </ul>
              )}
              <div className="job-plan-foot">
                <span className="text-muted">{describeSpan(plan)}. One visit per day, each checked against availability; a day this job already has this job type on is moved to these times. No emails go out.</span>
                <span className="spacer" />
                <button type="button" className="btn btn-sm btn-outline" onClick={addVisit} title="The full visit form: notes, status, a different client">Full form…</button>
                <button type="button" className="btn btn-sm btn-primary" onClick={submitPlan} disabled={plan.busy}>{plan.busy ? 'Booking…' : (planDays > 1 ? `Book ${planDays} days` : 'Book the visit')}</button>
              </div>
            </div>
          )}
          {attachV && (
            <div className="job-attach">
              {attachV.candidates.length === 0 ? (
                <p className="text-muted">{attachV.all ? 'No other visits to attach.' : 'No other visits for this client.'}</p>
              ) : (
                <div className="job-attach-row">
                  <select aria-label="Visit to attach" value={attachV.pick} onChange={(e) => setAttachV((a) => ({ ...a, pick: e.target.value }))}>
                    {attachV.candidates.map((a) => (
                      <option key={a.id} value={a.id}>
                        {formatApptDateLong(a.starts_at)} {formatApptTime(a.starts_at)} · {a.service_name || 'Visit'} · {String(a.status).replace('_', ' ')}{a.job_number ? ` · on ${a.job_number}` : ''}{attachV.all && a.client_name ? ` · ${a.client_name}` : ''}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-sm btn-primary" onClick={attachVisit} disabled={busy || !attachV.pick}>Attach</button>
                </div>
              )}
              {!attachV.all && job.customer_id && (
                <button type="button" className="btn-link job-attach-all" onClick={() => openAttachVisit(true)}>Show every client's visits</button>
              )}
              <p className="text-muted job-attach-hint">The visit moves here with any cost lines tied to it. A job left with nothing on it is removed.</p>
            </div>
          )}
          {job.visits.length === 0 ? <p className="text-muted">Nothing booked yet — use + Schedule, or attach a visit that already exists.</p> : (
            <div className="job-rows">
              {job.visits.map((v, i) => (
                <button key={v.id} type="button" className="job-item" onClick={() => setVisitModal({ appointment: v })}>
                  <div className="job-item-when"><strong>{formatApptDateLong(v.starts_at)}</strong><span className="text-muted">{formatApptRange(v.starts_at, v.ends_at)}{v.duration_minutes ? ` · ${fmtHours(v.duration_minutes)}` : ''}{live.length > 1 && LIVE(v) ? ` · visit ${live.indexOf(v) + 1} of ${live.length}` : ''}</span></div>
                  <span className="job-item-main">{v.service_name || 'Visit'} · {v.crew_names || v.staff_name || '—'}</span>
                  <span className={`status-badge status-${String(v.status).replace('_', '-')}`}>{String(v.status).replace('_', ' ')}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="card job-section-full">
        <JobCosts jobId={job.id} staff={staff} hideSummary onChange={(costs) => setJob((j) => ({ ...j, costs }))} onLightbox={(images, index) => setLightbox({ images, index })} />
      </section>

      {worksheet && (
        <section className="card job-section-full">
          <h2 className="card-title mb-md">Materials list <span className="text-muted job-ml-quote">· {worksheet.quote_number}</span></h2>
          <MaterialsList key={`${worksheet.quote_id}-${wsTick}`} quoteId={worksheet.quote_id} />
        </section>
      )}

      <section className="card job-section-full">
        <div className="job-photos-head">
          <h2 className="card-title">Photos ({job.photos.length})</h2>
          {job.photos.length > 0 && (
            <div className="job-socials">
              <label className="checkbox-label" title="Andrew and Leanne ask the customer in person; this records their yes">
                <input type="checkbox" checked={Boolean(job.socials_ok)} onChange={toggleSocials} />
                Customer said yes to their photos on our Facebook
              </label>
              {job.socials_ok ? <button type="button" className="btn btn-outline btn-sm" onClick={makePost}>Make a Facebook post</button> : null}
            </div>
          )}
        </div>
        {job.photos.length === 0 ? <p className="text-muted">No photos yet — add them on a visit.</p> : (
          <div className="job-photo-groups">
            {photoGroups.map((g) => (
              <div key={g.visit?.id || 'x'} className="job-photo-group">
                <p className="text-muted job-photo-label">{g.visit ? `${formatApptDateLong(g.visit.starts_at)} · ${g.visit.service_name || 'Visit'}` : 'Visit'}</p>
                <div className="job-photo-thumbs">
                  {g.photos.map((p, i) => (
                    <button key={p.id} type="button" className="job-photo-thumb" title={`${PHOTO_KIND_LABEL[p.kind] || p.kind}${p.original_name ? ` — ${p.original_name}` : ''}`} onClick={() => openPhotos(g, i)}>
                      <img src={imagesService.fileUrl(p.id, 'thumb')} alt={p.original_name || p.kind} />
                      <span className="job-photo-kind">{PHOTO_KIND_LABEL[p.kind] || p.kind}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <JobModal isOpen={editing} onClose={() => setEditing(false)} onSave={() => { setEditing(false); load(); }} job={job} />
      <QuoteModal
        isOpen={!!quoteModal}
        onClose={() => { setQuoteModal(null); load(); }}
        onSave={() => {}}
        quote={quoteModal?.quote || null}
        prefill={quoteModal?.prefill || null}
      />
      <AppointmentModal
        isOpen={!!visitModal}
        onClose={() => { setVisitModal(null); load(); }}
        onSave={() => load()}
        appointment={visitModal?.appointment || null}
        prefill={visitModal?.prefill || null}
      />
      {lightbox && <Lightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}
